import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// Function to extract YouTube video ID from URL
const extractYouTubeVideoId = (url) => {
    if (!url) return '';
    // If it's already an embed URL
    if (url.includes('youtube.com/embed/')) {
        return url.split('embed/')[1]?.split('?')[0];
    }
    // If it's a YouTube watch URL
    if (url.includes('youtube.com/watch?v=')) {
        return url.split('v=')[1]?.split('&')[0];
    }
    // If it's a YouTube short URL
    if (url.includes('youtube.com/shorts/')) {
        return url.split('shorts/')[1]?.split('?')[0];
    }
    // If it's a youtu.be URL
    if (url.includes('youtu.be/')) {
        return url.split('youtu.be/')[1]?.split('?')[0];
    }
    return '';
};

// Function to get YouTube embed URL
const getYouTubeEmbedUrl = (url) => {
    const videoId = extractYouTubeVideoId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
};

// Function to get YouTube thumbnail URL
const getYouTubeThumbnail = (url) => {
    const videoId = extractYouTubeVideoId(url);
    if (!videoId) return '';
    // Try maxresdefault first, fallback to hqdefault
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
};

// Video Card Component
const VideoCard = ({ piece }) => {
    const [showVideo, setShowVideo] = useState(false);
    const thumbnailUrl = getYouTubeThumbnail(piece.videoUrl);
    const embedUrl = getYouTubeEmbedUrl(piece.videoUrl);

    return (
        <div>
            {!showVideo && thumbnailUrl ? (
                <div 
                    className="relative w-full pb-[56.25%] h-0 overflow-hidden rounded-lg bg-gray-900 cursor-pointer group"
                    onClick={() => setShowVideo(true)}
                >
                    <img
                        src={thumbnailUrl}
                        alt={piece.title}
                        className="absolute top-0 left-0 w-full h-full object-cover"
                        onError={(e) => {
                            // Fallback to hqdefault if maxresdefault fails
                            const videoId = extractYouTubeVideoId(piece.videoUrl);
                            if (videoId) {
                                e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                            }
                        }}
                    />
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-50 transition-opacity">
                        <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform">
                            <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                            </svg>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="relative w-full pb-[56.25%] h-0 overflow-hidden rounded-lg bg-gray-900">
                    <iframe
                        className="absolute top-0 left-0 w-full h-full"
                        src={embedUrl}
                        title={piece.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    ></iframe>
                </div>
            )}
        </div>
    );
};

// Twitter Embed Component
const TwitterCard = ({ twitterUrl, isMain = false }) => {
    const [isDark, setIsDark] = useState(false);
    const tweetRef = React.useRef(null);
  
    useEffect(() => {
      const checkDarkMode = () => {
        setIsDark(document.documentElement.classList.contains('dark'));
      };
      checkDarkMode();
  
      const observer = new MutationObserver(checkDarkMode);
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
  
      return () => observer.disconnect();
    }, []);
  
    useEffect(() => {
      if (tweetRef.current && twitterUrl) {
        const timer = setTimeout(() => {
          if (window.twttr && window.twttr.widgets) {
            window.twttr.widgets.load(tweetRef.current);
          } else {
            const retryTimer = setTimeout(() => {
              if (window.twttr && window.twttr.widgets) {
                window.twttr.widgets.load(tweetRef.current);
              }
            }, 500);
            return () => clearTimeout(retryTimer);
          }
        }, 200);
        return () => clearTimeout(timer);
      }
    }, [twitterUrl]);
  
    if (!twitterUrl) return null;
  
    // Clean URL if needed
    const cleanUrl = twitterUrl.split('?')[0];
  
    return (
      <div
        className={`relative rounded-lg overflow-hidden mx-auto ${
          isMain ? 'w-full' : 'max-w-sm'
        }`}
        style={{
          transform: 'scale(0.9)', // squeeze slightly
          transformOrigin: 'top center',
        }}
        ref={tweetRef}
      >
        <blockquote
          className="twitter-tweet"
          data-theme={isDark ? 'dark' : 'light'}
          data-width="300"
          data-media-max-width="560"
          data-cards="hidden"
          data-conversation="none"
          data-align="center"
        >
          <a href={cleanUrl}>Loading Tweet...</a>
        </blockquote>
      </div>
    );
  };

const PianoPieces = () => {
    // Piano pieces data with video URLs and Twitter links
    const pianoPieces = [
        {
            id: 1,
            title: 'Die With a Smile',
            description: 'A beautiful song of Bruno Mars and Lady Gaga',
            videoUrl: 'https://youtube.com/shorts/Bp7gJ3tmTvs?si=qJtHRsi3QzFKdKjh',
            twitterUrl: '', // Add Twitter post URL here (e.g., 'https://twitter.com/username/status/1234567890')
        },
        {
            id: 2,
            title: 'Dhadak Title Song',
            description: 'An emotional masterpiece of Ajay-Atul',
            videoUrl: '',
            twitterUrl: 'https://twitter.com/dwivedi_aryann/status/1931215784795427301', // Updated Twitter URL
        },
        {
            id: 3,
            title: 'Snowman',
            description: 'Song of Sia',
            videoUrl: 'https://youtu.be/CCcJ3Cg_NeQ?si=sRVPw-GJKZJcgm2s',
            twitterUrl: '', // Add Twitter post URL here
        },
        {
            id: 4,
            title: 'Main Agar Kahoon',
            description: 'Song of Sonu Nigam',
            videoUrl: '',
            twitterUrl: 'https://twitter.com/dwivedi_aryann/status/1858092461903815099',
        },
    ];

    // Load Twitter widget script on mount
    useEffect(() => {
        // Check if script is already loaded
        if (window.twttr && window.twttr.widgets) {
            // If already loaded, just load widgets
            window.twttr.widgets.load();
            return;
        }

        // Check if script tag already exists
        if (document.getElementById('twitter-wjs')) {
            // Script exists, wait for it to load
            const checkTwttr = setInterval(() => {
                if (window.twttr && window.twttr.widgets) {
                    window.twttr.widgets.load();
                    clearInterval(checkTwttr);
                }
            }, 100);
            return () => clearInterval(checkTwttr);
        }

        // Create script element
        const script = document.createElement('script');
        script.src = 'https://platform.twitter.com/widgets.js';
        script.async = true;
        script.charset = 'utf-8';
        script.id = 'twitter-wjs';
        
        // Add script to document
        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript && firstScript.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);
        } else {
            document.body.appendChild(script);
        }

        // Initialize Twitter widgets when script loads
        script.onload = () => {
            if (window.twttr && window.twttr.widgets) {
                window.twttr.widgets.load();
            }
        };
    }, []);

    // Animation variants
    const containerVariant = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
            },
        },
    };

    const itemVariant = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
    };

    return (
        <section className="bg-white dark:bg-gray-900 py-12" id="piano">
            <div className="container px-6 py-10 mx-auto">
                <motion.h1
                    className="text-3xl font-semibold text-center text-gray-800 dark:text-white"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    Piano Pieces
                </motion.h1>
                <motion.p
                    className="mt-4 text-center text-gray-600 dark:text-gray-400"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    Listen to my piano performances and compositions.
                </motion.p>

                <motion.div
                    className="grid grid-cols-1 gap-8 mt-8 md:grid-cols-2 lg:grid-cols-3"
                    variants={containerVariant}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                >
                    {pianoPieces.map((piece) => {
                        const hasVideo = piece.videoUrl && piece.videoUrl.trim() !== '';
                        const hasTwitter = piece.twitterUrl && piece.twitterUrl.trim() !== '';
                        
                        return (
                            <motion.div
                                key={piece.id}
                                className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700 p-4"
                                variants={itemVariant}
                            >
                                {/* Title and Description */}
                                <div className="mb-4">
                                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-1">
                                        {piece.title}
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {piece.description}
                                    </p>
                                </div>

                                {/* YouTube Video Container - Show if video exists */}
                                {hasVideo && (
                                    <div className="mb-4">
                                        <VideoCard piece={piece} />
                                    </div>
                                )}
                                
                                {/* Twitter Embed - Show if Twitter URL exists */}
                                {hasTwitter && (
                                    <TwitterCard 
                                        twitterUrl={piece.twitterUrl} 
                                        isMain={!hasVideo}
                                    />
                                )}
                            </motion.div>
                        );
                    })}
                </motion.div>
            </div>
        </section>
    );
};

export default PianoPieces;